// Hook for fetching demo firehose data per-widget
// Each widget instance manages its own stories array

import { useState, useEffect, useCallback, useRef } from "react";
import { DemoSource, DEMO_SOURCES } from ".";
import { getDemoNews } from "./demoClient";
import demoSSEClient from "./demoSSEClient";
import { NewsFeedItem } from "../firehose/models";
import { FETCH_STATUS } from "@/shared/api";

interface UseDemoFeedOptions {
  /** Sources to fetch/filter for this widget instance */
  sources: readonly DemoSource[];
}

interface UseDemoFeedResult {
  stories: NewsFeedItem[];
  isLoading: boolean;
  isError: boolean;
  hasMore: boolean;
  loadNext: () => void;
}

/**
 * Hook to fetch demo firehose data for a specific widget.
 * Each widget instance has its own stories array.
 *
 * @param options - Configuration including which sources to fetch
 * @returns Stories and loading state for this widget instance
 */
export const useDemoFeed = ({
  sources,
}: UseDemoFeedOptions): UseDemoFeedResult => {
  const [stories, setStories] = useState<NewsFeedItem[]>([]);
  const [status, setStatus] = useState<FETCH_STATUS>(FETCH_STATUS.IDLE);
  const isMountedRef = useRef(true);

  // Determine if this is a single-source widget or all sources
  const isSingleSource = sources.length === 1;
  const isAllSources = sources.length === DEMO_SOURCES.length;

  // Initial fetch
  useEffect(() => {
    isMountedRef.current = true;

    const fetchInitialData = async () => {
      setStatus(FETCH_STATUS.LOADING);

      try {
        let data: NewsFeedItem[];

        if (isSingleSource) {
          // Fetch with specific source filter
          data = await getDemoNews(sources[0]);
        } else {
          // Fetch all and filter client-side
          data = await getDemoNews();
          if (!isAllSources) {
            data = data.filter((story) =>
              sources.includes(story.backend_source as DemoSource)
            );
          }
        }

        // Sort data by date descending
        if (data && data.length > 0)
          data.sort((a, b) => Number(b.created_utc) - Number(a.created_utc));

        if (isMountedRef.current) {
          setStories(data);
          setStatus(FETCH_STATUS.SUCCESS);
        }
      } catch (error) {
        console.error("Demo feed fetch error:", error);
        if (isMountedRef.current) {
          setStatus(FETCH_STATUS.ERROR);
        }
      }
    };

    fetchInitialData();

    return () => {
      isMountedRef.current = false;
    };
  }, [sources, isSingleSource, isAllSources]);

  // SSE subscription for real-time updates
  useEffect(() => {
    // Start SSE connection
    demoSSEClient.start({});

    const unsubscribe = demoSSEClient.subscribe(
      "message",
      (newStory: NewsFeedItem) => {
        // Only add if source is in allowed list for this widget
        const storySource = newStory.backend_source as DemoSource;
        if (sources.includes(storySource)) {
          // Mark as new for UI highlighting
          newStory.isNew = true;
          newStory.received_at = Date.now();

          setStories((prev) => {
            // Dedupe by ID
            if (prev.some((s) => s.id === newStory.id)) {
              return prev;
            }
            // Add to beginning (most recent first)
            return [newStory, ...prev];
          });
        }
      }
    );

    return () => {
      unsubscribe();
    };
  }, [sources]);

  // Load more function (demo API doesn't support pagination, so this is a no-op)
  const loadNext = useCallback(() => {
    // Demo endpoint returns all available data, no pagination
    // This is kept for API compatibility with authenticated firehose
  }, []);

  return {
    stories,
    isLoading: status === FETCH_STATUS.LOADING,
    isError: status === FETCH_STATUS.ERROR,
    hasMore: false, // Demo API doesn't support pagination
    loadNext,
  };
};

export default useDemoFeed;
